import React, { useState, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useCandidatesApi } from "src/_common/hooks/actions/candidates/appCandidatesApiHook";
import ViewProfileModal from "../job-details/_common/ViewProfileModal";
import VideoCVFilters from "../video-cv/VideoCVFilters";
import ProgressIndicator from "../job-details/_common/ProgressIndicator";

interface Jobseeker {
  jobseeker: {
    id: number;
    email?: string;
    mobile_number?: string;
  };
  document_full_path: {
    profile_pic: string;
    video_file: string;
    resume_file?: string;
  };
  basic_details: {
    full_name: string;
    cv_headline: string;
    key_skills_custome: string[];
    current_location: string;
    profile_summary?: string;
  };
  employment_details?: any[];
  education_details?: any[];
  certification_details?: any[];
  course_details?: any[];
  jobseeker_public_profiles?: any[];
}

interface Pagination {
  returned_count: number;
  total: number;
  current_page: number;
  total_pages: number;
  per_page: number;
  has_more: boolean;
  has_next_page?: boolean;
  has_previous_page?: boolean;
}

const CandidatesMain: React.FC = () => {
  const { t } = useTranslation("candidates");
  const { callGetJobseekers } = useCandidatesApi();

  // State variables
  const [jobseekers, setJobseekers] = useState<Jobseeker[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [selectedJobseeker, setSelectedJobseeker] = useState<any>(null);
  const itemsPerPage = 12;

  const [sortBy, setSortBy] = useState<string>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false); // Track if filters have been applied
  const [appliedFilters, setAppliedFilters] = useState<{
    keywords: string;
    location: string;
    country_id: string;
    skills: string;
  }>({
    keywords: "",
    location: "",
    country_id: "",
    skills: "",
  });

  // Fetch jobseekers only when filters have been applied
  useEffect(() => {
    if (hasSearched) {
      fetchJobseekers(false);
    }
  }, [sortBy, appliedFilters, hasSearched]);

  const fetchJobseekers = (isLoadMore: boolean = false, pageToFetch?: number) => {
    const params: any = {
      page: pageToFetch || (isLoadMore ? currentPage : 1),
      per_page: itemsPerPage,
      sort_order: sortBy === "newest" ? "desc" : "asc",
    };

    // Add filters if they exist
    if (appliedFilters.keywords) {
      params.keyword = appliedFilters.keywords;
    }
    if (appliedFilters.skills) {
      params.skills = appliedFilters.skills;
    }
    if (appliedFilters.country_id) {
      params.country = appliedFilters.country_id;
    }

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    callGetJobseekers(
      params,
      (message: string, data: any) => {
        if (isLoadMore) {
          setLoadingMore(false);
          // Append new jobseekers to existing list
          setJobseekers((prev) => [...prev, ...(data.jobseekers || [])]);
        } else {
          setLoading(false);
          // Replace jobseekers list
          setJobseekers(data.jobseekers || []);
        }
        setPagination(data.pagination || null);
        console.log("Candidates fetched successfully:", data);
      },
      (error: string) => {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
          setJobseekers([]);
          setPagination(null);
        }
        console.error("Error fetching candidates:", error);
      }
    );
  };

  const handleApplyFilters = (filters: { keywords: string; location: string; country_id: string; skills: string }) => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setHasSearched(true); // Mark that search has been initiated
  };

  const handleResetFilters = () => {
    setAppliedFilters({
      keywords: "",
      location: "",
      country_id: "",
      skills: "",
    });
    setCurrentPage(1);
    setSortBy("newest");
    setHasSearched(false); // Reset search state
    setJobseekers([]); // Clear the list
    setPagination(null);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchJobseekers(true, nextPage);
  };

  const handleViewProfile = (jobseeker: any) => {
    console.log("Original jobseeker data:", jobseeker);

    // The API returns data in this structure:
    // - jobseeker.basic_details is an object (not array in candidates API)
    // - jobseeker.jobseeker contains account info
    // - jobseeker.education_details, employment_details, etc. are arrays

    const basicDetails = jobseeker.basic_details || {};
    const jobseekerAccount = jobseeker.jobseeker || {};

    // Extract gender information
    const getGenderLabel = (genderId: number) => {
      const genderMap: { [key: number]: string } = {
        1: "Male",
        2: "Female",
        3: "Other",
      };
      return genderMap[genderId] || "-";
    };

    // Format skills from key_skills_custome or key_skills array
    const skills = basicDetails.key_skills_custome || basicDetails.key_skills || [];
    const skillsString = Array.isArray(skills) ? skills.join(", ") : skills;

    // Format languages
    const languages = basicDetails.known_languages || "-";

    // Format the jobseeker data to match the ViewProfileModal's expected structure
    const formattedData = {
      // Top-level fields that ViewProfileModal expects
      id: jobseeker.id || jobseekerAccount.id,
      jobseekerId: jobseeker.jobseekerId || jobseekerAccount.id,
      name: jobseeker.name || basicDetails.full_name || basicDetails.name,
      avatar: jobseeker.avatar || jobseeker.document_full_path?.profile_pic,
      position: jobseeker.position || basicDetails.cv_headline || basicDetails.current_designation || "-",
      resume_file: jobseeker.document_full_path?.system_resume_file,
      video_file: jobseeker.video_file || jobseeker.document_full_path?.video_file,

      // Profile header section fields
      currentDesignation: basicDetails.cv_headline || basicDetails.current_designation || "",
      currentLocation: basicDetails.current_location || "",
      gender: getGenderLabel(basicDetails.gender_id),
      totalExperienceYear: basicDetails.total_experience_year || null,
      currentSalary: basicDetails.current_salary || null,
      currentSalaryCurrency: basicDetails.current_salary_currency || null,

      // Skills and profile summary
      skills: skillsString,
      profileSummary: basicDetails.profile_summary || "",

      // Personal details
      dateOfBirth: basicDetails.dob || basicDetails.date_of_birth || "",
      languages: languages,

      // Basic details object (for buttons and other info in modal)
      basicDetails: {
        jobseeker_id: jobseekerAccount.id,
        name: basicDetails.full_name || basicDetails.name,
        first_name: basicDetails.first_name || "",
        last_name: basicDetails.last_name || "",
        email: jobseekerAccount.email || jobseekerAccount.deleted_email || "",
        mobile_number: jobseekerAccount.mobile_number || jobseekerAccount.deleted_mobile_number || "",
        current_designation: basicDetails.cv_headline || basicDetails.current_designation,
        current_location: basicDetails.current_location || "",
        key_skills: skills,
        profile_summary: basicDetails.profile_summary || "",
        resume_file: jobseeker.document_full_path?.system_resume_file,
        video_file: jobseeker.document_full_path?.video_file,
        preferred_country: basicDetails.preferred_country || [],
        driving_license: basicDetails.driving_license || false,
        preferred_work_type: basicDetails.preferred_work_type || "",
        dob: basicDetails.dob || basicDetails.date_of_birth || "",
        gender_id: basicDetails.gender_id,
        known_languages: basicDetails.known_languages || "",
      },

      // Jobseeker account info (for secondary contact in personal details section)
      jobseekerAccount: {
        secondary_email: jobseekerAccount.secondary_email || "",
        secondary_mobile_number: jobseekerAccount.secondary_mobile_number || "",
        secondary_country_code: jobseekerAccount.secondary_country_code || "",
      },

      // Employment/Experience details (from employment_details or experience array)
      employmentDetails: jobseeker.employment_details || jobseeker.experience || [],

      // Education details (from education_details, education, or course_details)
      educationDetails: jobseeker.education_details || jobseeker.education || jobseeker.course_details || [],

      // Course details (separate from education if available)
      courseDetails: jobseeker.course_details || [],

      // Project details (from project_details or projects array)
      projectDetails: jobseeker.project_details || jobseeker.projects || [],

      // Certifications (from certification_details or certifications)
      certificateDetails: jobseeker.certification_details || jobseeker.certifications || [],

      // Document full paths
      document_full_path: jobseeker.document_full_path || {},

      // Public profile information
      jobseeker_public_profiles: jobseeker.jobseeker_public_profiles || [],
    };

    console.log("Formatted candidate details for modal:", formattedData);
    setSelectedJobseeker(formattedData);
    setIsViewProfileOpen(true);
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      <div className="video-cv-container">
        <div className="video-cv-content">
          {/* Filters Sidebar */}
          <VideoCVFilters onApplyFilters={handleApplyFilters} onResetFilters={handleResetFilters} context="candidate" />

          {/* Main Content */}
          <div className="video-cv-main">
            {!hasSearched ? (
              // Show ProgressIndicator when no filters have been applied
              <ProgressIndicator stage="candidates" />
            ) : loading ? (
              <div className="loading-container">
                <p>{t("candidates.loading", "Loading...")}</p>
              </div>
            ) : jobseekers.length > 0 ? (
              <>
                <div className="results-header">
                  <div className="results-info">
                    <h2>{t("candidates.title", "Candidates")}</h2>
                    <p className="results-count">
                      {t("candidates.showing", "Showing")} {jobseekers.length} {t("candidates.results", "results")}
                    </p>
                  </div>
                  <div className="sort-controls">
                    <label>{t("candidates.sortBy", "Sort by:")}</label>
                    <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} className="sort-select">
                      <option value="newest">{t("candidates.newest", "Newest")}</option>
                      <option value="oldest">{t("candidates.oldest", "Oldest")}</option>
                    </select>
                  </div>
                </div>

                {(
                  <>
                    <div className="candidates-grid candidates-list-view">
                      {jobseekers.map((jobseeker: Jobseeker) => (
                        <div key={jobseeker.jobseeker.id} className="candidate-card candidate-list-card">
                          <div className="candidate-info">
                            <div className="candidate-header">
                              <img
                                src={jobseeker.document_full_path.profile_pic || "/images/placeholder.jpg"}
                                alt={jobseeker.basic_details.full_name}
                                className="candidate-avatar"
                              />
                              <div className="candidate-details">
                                <h3 className="candidate-name">{jobseeker?.basic_details?.full_name}</h3>
                                <p className="candidate-position">{jobseeker?.basic_details?.cv_headline}</p>
                              </div>
                            </div>

                            {jobseeker?.basic_details?.current_location && (
                              <div className="candidate-meta">
                                <div className="meta-item">
                                  <i className="fa fa-map-marker"></i>
                                  <span>{jobseeker?.basic_details?.current_location}</span>
                                </div>
                              </div>
                            )}

                            <div className="candidate-actions">
                              <button className="view-profile-button" onClick={() => handleViewProfile(jobseeker)}>
                                {t("candidates.viewProfile", "View Profile")}
                              </button>
                              {jobseeker?.jobseeker?.email && (
                                <button
                                  className="message-button"
                                  onClick={() => (window.location.href = `mailto:${jobseeker.jobseeker.email}`)}
                                >
                                  <i className="fa fa-envelope"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Load More Button */}
                    {pagination?.has_more && (
                      <div className="load-more-container">
                        <button className="load-more-button" onClick={handleLoadMore} disabled={loadingMore}>
                          {loadingMore ? (
                            <>
                              <i className="fa fa-spinner fa-spin"></i>
                              {t("candidates.loadingMore", "Loading More...")}
                            </>
                          ) : (
                            <>
                              <i className="fa fa-chevron-down"></i>
                              {t("candidates.loadMore", "Load More")}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <ProgressIndicator stage="no-candidates" />
            )}
          </div>
        </div>
      </div>

      {/* View Profile Modal */}
      {isViewProfileOpen && selectedJobseeker && (
        <ViewProfileModal
          isOpen={isViewProfileOpen}
          onClose={() => setIsViewProfileOpen(false)}
          jobseekerId={selectedJobseeker.basicDetails?.jobseeker_id}
          candidateDetails={selectedJobseeker}
        />
      )}
    </div>
  );
};

export default CandidatesMain;
